import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { exportScanResult } from "views/pages/ScanResultsPage/useScanResultsExport";
import { ScanExportStatus } from "views/pages/ScanResultsPage/types";

export interface ScanExportItem {
  /** The scan result's path, which is also its identity in the browser. */
  srPath: string;
  name: string;
  waferPaths: string[];
  status: ScanExportStatus;
  /** Why it failed, when it did. */
  error?: string;
  /** The datasets-relative folder it was exported to, once added. */
  folderName?: string;
  /** When the current export started, for the elapsed time on the row. */
  startedAt?: number;
}

/**
 * Exports scan results to ADC one at a time, in the background, while the user carries
 * on browsing or adding paths.
 *
 * Sequential on purpose: each export copies every defect image on the DataServer host,
 * so running them at once would just contend. The queue lives as long as the Add Data
 * step; the export call itself is synchronous on the server, so leaving the page drops
 * whatever is still in flight - the wizard blocks Continue and warns on leave meanwhile.
 *
 * `onExported` receives each folder as soon as its export lands, so it shows up in the
 * folder table without waiting for the rest.
 */
export default function useScanExportQueue(
  onExported: (folderName: string) => void,
) {
  const [items, setItems] = useState<ScanExportItem[]>([]);
  const running = useRef(false);
  const unmounted = useRef(false);
  // Read through a ref so a caller passing a fresh closure each render does not restart
  // anything, and the export that resolves later still reaches the current handler.
  const onExportedRef = useRef(onExported);
  onExportedRef.current = onExported;

  useEffect(() => {
    // Reset on mount too: StrictMode mounts, unmounts and mounts again in development.
    unmounted.current = false;
    return () => {
      unmounted.current = true;
    };
  }, []);

  const update = (srPath: string, patch: Partial<ScanExportItem>) =>
    setItems((current) =>
      current.map((item) =>
        item.srPath === srPath ? { ...item, ...patch } : item,
      ),
    );

  useEffect(() => {
    if (running.current) return;
    const next = items.find((item) => item.status === "queued");
    if (!next) return;

    running.current = true;
    update(next.srPath, { status: "exporting", startedAt: Date.now() });
    exportScanResult(next.waferPaths)
      .then((folderName) => {
        if (unmounted.current) return;
        update(next.srPath, { status: "added", folderName, error: undefined });
        onExportedRef.current(folderName);
      })
      .catch((e: Error) => {
        if (unmounted.current) return;
        update(next.srPath, { status: "failed", error: e.message });
      })
      .finally(() => {
        running.current = false;
        // Nudge the effect to pick up the next queued item.
        if (!unmounted.current) setItems((current) => [...current]);
      });
  }, [items]);

  /** Queue scan results; one that failed before is queued again in its place. */
  const enqueue = useCallback(
    (entries: { srPath: string; name: string; waferPaths: string[] }[]) =>
      setItems((current) => {
        const replaced = new Set(entries.map((entry) => entry.srPath));
        return [
          ...current.filter((item) => !replaced.has(item.srPath)),
          ...entries.map((entry) => ({
            ...entry,
            status: "queued" as const,
          })),
        ];
      }),
    [],
  );

  const retry = useCallback(
    (srPath?: string) =>
      setItems((current) =>
        current.map((item) =>
          item.status === "failed" && (!srPath || item.srPath === srPath)
            ? { ...item, status: "queued", error: undefined }
            : item,
        ),
      ),
    [],
  );

  /** Drop a failed or still-queued item. One being exported cannot be stopped. */
  const remove = useCallback(
    (srPath: string) =>
      setItems((current) =>
        current.filter(
          (item) =>
            item.srPath !== srPath ||
            item.status === "exporting" ||
            item.status === "added",
        ),
      ),
    [],
  );

  const statusByPath = useMemo(
    () =>
      Object.fromEntries(
        items.map((item) => [item.srPath, item.status]),
      ) as Record<string, ScanExportStatus>,
    [items],
  );

  const pendingCount = items.filter(
    (item) => item.status === "queued" || item.status === "exporting",
  ).length;

  return { items, statusByPath, pendingCount, enqueue, retry, remove };
}
